// Domestic Helper Files Storage
// Temporary in-memory storage for document files during the policy flow
// Files are stored here because Redux cannot serialize File/Blob objects

// Storage map for domestic helper documents
const domesticHelperFiles = new Map();

// File keys for domestic helper documents
export const DOMESTIC_HELPER_FILE_KEYS = {
  PASSPORT: 'domesticHelper_passport',
  WORK_PERMIT: 'domesticHelper_work_permit',
};

/**
 * Store a file for domestic helper policy
 * @param {string} key - File key (use DOMESTIC_HELPER_FILE_KEYS)
 * @param {File} file - The file to store
 */
export const storeDomesticHelperFile = (key, file) => {
  if (file instanceof File || file instanceof Blob) {
    domesticHelperFiles.set(key, file);
    console.log('[DomesticHelper Storage] Stored file:', key, file.name || 'blob');
  }
};

/**
 * Get a stored file
 * @param {string} key - File key
 * @returns {File|null} The stored file or null
 */
export const getDomesticHelperFile = (key) => {
  return domesticHelperFiles.get(key) || null;
};

/**
 * Get all stored domestic helper files
 * @returns {Object} Object with all stored files { passport, workPermit }
 */
export const getAllDomesticHelperFiles = () => {
  return {
    passport: domesticHelperFiles.get(DOMESTIC_HELPER_FILE_KEYS.PASSPORT) || null,
    workPermit: domesticHelperFiles.get(DOMESTIC_HELPER_FILE_KEYS.WORK_PERMIT) || null,
  };
};

/**
 * Get all files as an array for Vista submission
 * @returns {File[]} Array of files in order [passport, workPermit]
 */
export const getDomesticHelperFilesArray = () => {
  const files = [];
  const passport = domesticHelperFiles.get(DOMESTIC_HELPER_FILE_KEYS.PASSPORT);
  const workPermit = domesticHelperFiles.get(DOMESTIC_HELPER_FILE_KEYS.WORK_PERMIT);

  if (passport) files.push(passport);
  if (workPermit) files.push(workPermit);

  return files;
};

/**
 * Get document types array matching the files order
 * @returns {string[]} Array of document type labels
 */
export const getDomesticHelperDocumentTypes = () => {
  const types = [];
  if (domesticHelperFiles.has(DOMESTIC_HELPER_FILE_KEYS.PASSPORT)) types.push('Passport');
  if (domesticHelperFiles.has(DOMESTIC_HELPER_FILE_KEYS.WORK_PERMIT)) types.push('Work Permit (domestic helper)');
  return types;
};

/**
 * Remove a specific file
 * @param {string} key - File key to remove
 */
export const removeDomesticHelperFile = (key) => {
  domesticHelperFiles.delete(key);
  console.log('[DomesticHelper Storage] Removed file:', key);
};

/**
 * Clear all stored domestic helper files
 * Call this after successful policy creation or when user exits flow
 */
export const clearDomesticHelperFiles = () => {
  domesticHelperFiles.clear();
  console.log('[DomesticHelper Storage] Cleared all files');
};

/**
 * Get count of stored files
 * @returns {number} Number of stored files
 */
export const getDomesticHelperFilesCount = () => {
  return domesticHelperFiles.size;
};

/**
 * Check if all required files are stored
 * @returns {boolean} True if both files are present
 */
export const hasAllDomesticHelperFiles = () => {
  return (
    domesticHelperFiles.has(DOMESTIC_HELPER_FILE_KEYS.PASSPORT) &&
    domesticHelperFiles.has(DOMESTIC_HELPER_FILE_KEYS.WORK_PERMIT)
  );
};

export default {
  storeDomesticHelperFile,
  getDomesticHelperFile,
  getAllDomesticHelperFiles,
  getDomesticHelperFilesArray,
  getDomesticHelperDocumentTypes,
  removeDomesticHelperFile,
  clearDomesticHelperFiles,
  getDomesticHelperFilesCount,
  hasAllDomesticHelperFiles,
  DOMESTIC_HELPER_FILE_KEYS,
};
